import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { runsApi, type SrDetail } from '../../lib/runsApi';
import { TexecPicker } from './TexecPicker';
import { TrainingBadge } from '../../components/TrainingBadge';

interface Props {
  onClose: () => void;
}

/**
 * Lets the user paste any SR key (e.g. one not assigned to them) and pool a
 * run from it. Resolution happens via the existing
 * `/api/runs/sales-orders/:srKey` endpoint; on success we hand the resolved
 * detail to the shared `TexecPicker`.
 */
export function CustomSrDialog({ onClose }: Props) {
  const navigate = useNavigate();
  const [srInput, setSrInput] = useState('');
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sr, setSr] = useState<SrDetail | null>(null);

  // Accept both live (SR-) and training (XOT-) parent orders.
  const valid = /^(SR|XOT)-\d+$/i.test(srInput.trim());

  const onResolve = async () => {
    if (!valid) return;
    setResolving(true);
    setError(null);
    setSr(null);
    try {
      const data = await runsApi.loadSalesOrder(srInput.trim().toUpperCase());
      setSr(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setResolving(false);
    }
  };

  const onCreated = (runId: string) => {
    onClose();
    navigate(`/runs/${runId}/phase/1`);
  };

  const reset = () => {
    setSr(null);
    setError(null);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>Load a custom SR</h2>
          <span className="spacer" />
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>

        {!sr && (
          <>
            <p className="muted small" style={{ marginTop: 0 }}>
              Paste any SR or XOT (training) key. It does not need to be assigned to you. The
              execution picker appears once Jira resolves the order.
            </p>
            <div className="row" style={{ gap: 8 }}>
              <input
                placeholder="SR-12345 or XOT-12345"
                value={srInput}
                onChange={(e) => setSrInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && valid) onResolve();
                }}
                style={{ flex: 1 }}
                autoFocus
              />
              {valid && <TrainingBadge keyValue={srInput.trim()} />}
              <button
                className="btn btn-primary"
                disabled={!valid || resolving}
                onClick={onResolve}
              >
                {resolving ? 'Loading...' : 'Load'}
              </button>
            </div>
            {error && (
              <div className="card" style={{ borderColor: 'var(--bad)', marginTop: 12 }}>
                {error}
              </div>
            )}
          </>
        )}

        {sr && (
          <>
            <TexecPicker
              srKey={sr.srKey}
              initial={sr}
              onCreated={onCreated}
              onCancel={reset}
            />
          </>
        )}
      </div>
    </div>
  );
}
